import React, { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Formik, FormikProps } from 'formik'
import moment from 'moment'
import * as Yup from 'yup'

import { ProductIds } from 'core'
import addProductToCompany, {
  AddProductToCompanyInterface,
} from 'core/application/adminDashboard/ourProducts/addProductToCompany'
import { GetProductsToBuyResponse } from 'core/application/adminDashboard/ourProducts/productsToBuy'
import { SubscriptionPlansForPackageResponse } from 'core/application/subscriptions/getSubscriptionPlansForPackage'

import Modal from 'common/components/feedback/Modal'

import { Alert, Button, Col, Input, MonthPicker, Row } from 'common/components'
import { useCommandRequest } from 'common/hooks'

import SubscriptionTypes from './SubscriptionTypes'

type FormikData = {
  groupHealthPlanName: string
  groupHealthPlanStartDate: any
}

type PurchaseProductModalProps = {
  isOpen: boolean
  onClose: () => void
  switchToProduct: () => Promise<void>
  subscriptionPackageCode: string
  product: GetProductsToBuyResponse
  switchLoading: boolean
}

const formValidator = (translate: any) =>
  Yup.object({
    groupHealthPlanStartDate: Yup.date()
      .required(translate('general.required'))
      .typeError(translate('general.required')),
    groupHealthPlanName: Yup.string()
      .trim()
      .required(translate('general.required'))
      .typeError(translate('general.required')),
  })

const PurchaseProductModal = ({
  isOpen,
  subscriptionPackageCode,
  product,
  switchToProduct,
  switchLoading,
  onClose,
}: PurchaseProductModalProps) => {
  const { t: translate } = useTranslation()
  const [selectedSubscriptionPlan, setSelectedSubscriptionPlan] = useState<SubscriptionPlansForPackageResponse>(null)
  const [selectedDiscountCode, setSelectedDiscountCode] = useState<string | null>(
    product.crossSaleDiscountCode ?? product.crossSaleDiscountCodeYearly,
  )
  const [sendSaveAddProduct, loading] = useCommandRequest<AddProductToCompanyInterface>()
  const isGchra = product.id === ProductIds.GroupHraId

  const handleModalSubmit = async (formik: FormikProps<FormikData>) => {
    const { groupHealthPlanName, groupHealthPlanStartDate } = formik.values

    const payload = isGchra
      ? {
          groupHealthPlanName,
          groupHealthStartDate: groupHealthPlanStartDate,
          planCode: selectedSubscriptionPlan.code,
          productId: product.id,
          discountCode: selectedDiscountCode,
        }
      : {
          planCode: selectedSubscriptionPlan.code,
          productId: product.id,
          discountCode: selectedDiscountCode,
        }

    const result = await sendSaveAddProduct(() => addProductToCompany(payload))

    if (result.completed) {
      await switchToProduct()
    }
  }

  const footer = (formik: FormikProps<FormikData>) => (
    <div>
      <Button
        onClick={() => {
          onClose()
        }}
      >
        {translate('general.cancel')}
      </Button>
      <Button
        disabled={!formValidator(translate).isValidSync(formik.values) && isGchra}
        type="primary"
        htmlType="submit"
        className="ml-8"
        loading={loading || switchLoading}
        onClick={() => handleModalSubmit(formik)}
      >
        {translate('general.submit')}
      </Button>
    </div>
  )

  return (
    <Formik
      validationSchema={formValidator(translate)}
      enableReinitialize={true}
      initialValues={{ groupHealthPlanName: '', groupHealthPlanStartDate: null }}
      onSubmit={() => {}}
    >
      {(formik) => (
        <Modal
          onCancel={onClose}
          visible={isOpen}
          title={translate('admin-dashboard.our-products.purchase-modal').replace('[name]', product.name)}
          footer={footer(formik)}
          width={800}
        >
          <form onSubmit={formik.handleSubmit}>
            {isGchra && (
              <Row className="form__row mb-8">
                <Col span={12}>
                  <Input
                    label={translate('admin-signup.company-details.health-plan.insurance-plan-label')}
                    name="groupHealthPlanName"
                    placeholder={translate('admin-signup.company-details.health-plan.insurance-plan-placeholder')}
                  />
                </Col>
                <Col span={11} offset={1} className="">
                  <MonthPicker
                    label={translate('admin-signup.company-details.health-plan.start-date-label')}
                    name="groupHealthPlanStartDate"
                    size="large"
                    disabledDate={(d) =>
                      !d ||
                      d.isBefore(moment().subtract(10, 'month').subtract(1, 'day')) ||
                      d.isAfter(moment().add(5, 'month'))
                    }
                    id="groupHealthPlanStartDate"
                    format="MM-YYYY"
                    placeholder=""
                  />
                </Col>
              </Row>
            )}

            <SubscriptionTypes
              selectedSubscriptionPlan={selectedSubscriptionPlan}
              subscriptionPackageCode={subscriptionPackageCode}
              discountCode={product.crossSaleDiscountCode}
              discountCodeYearly={product.crossSaleDiscountCodeYearly}
              onSubscriptionPlanChange={(value) => setSelectedSubscriptionPlan(value)}
              onDiscountCodeChange={(value) => setSelectedDiscountCode(value)}
            />

            <Alert
              className="mt-8 py-8 flex flex__center"
              type="info"
              showIcon
              description={translate('admin-dashboard.our-products.payment-method')}
            />
          </form>
        </Modal>
      )}
    </Formik>
  )
}

export default PurchaseProductModal
